import axios from 'axios';

const BASE = '/api';

function getHeaders() {
  const token = localStorage.getItem('adminToken');
  return { Authorization: `Bearer ${token}` };
}

// Global 401/403 handler — clears stale tokens and forces re-login
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      const token = localStorage.getItem('adminToken');
      // Only clear if it looks like a stale plain token (not a JWT)
      if (token && !token.startsWith('ey')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('currentUser');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Auth
  login: (username, password) =>
    axios.post(`${BASE}/auth/login`, { username, password }),

  getMe: () =>
    axios.get(`${BASE}/auth/me`, { headers: getHeaders() }),

  changePassword: (currentPassword, newPassword) =>
    axios.put(`${BASE}/auth/password`, { currentPassword, newPassword }, { headers: getHeaders() }),

  // User management (admin only)
  getUsers: () =>
    axios.get(`${BASE}/users`, { headers: getHeaders() }),

  createUser: (data) =>
    axios.post(`${BASE}/users`, data, { headers: getHeaders() }),

  updateUser: (id, data) =>
    axios.put(`${BASE}/users/${id}`, data, { headers: getHeaders() }),

  deleteUser: (id) =>
    axios.delete(`${BASE}/users/${id}`, { headers: getHeaders() }),

  // Auth check (legacy — kept for compat)
  checkAuth: (token) =>
    axios.get(`${BASE}/agents`, { headers: { Authorization: `Bearer ${token}` } }),

  // Devices
  getDevices: () =>
    axios.get(`${BASE}/agents`, { headers: getHeaders() }),

  getConnectionStatus: () =>
    axios.get(`${BASE}/agents/connection-status`, { headers: getHeaders() }),

  // Patches
  getAllPatches: () =>
    axios.get(`${BASE}/patches/all`, { headers: getHeaders() }),

  createPatch: (data) =>
    axios.post(`${BASE}/patches`, data, { headers: getHeaders() }),

  fetchPatchUpdates: () =>
    axios.post(`${BASE}/patches/fetch-updates`, {}, { headers: getHeaders() }),

  validateUrls: () =>
    axios.get(`${BASE}/patches/validate-urls`, { headers: getHeaders() }),

  // Tasks
  assignPatch: (deviceId, patchId) =>
    axios.post(`${BASE}/tasks/assign`, { deviceId, patchId }, { headers: getHeaders() }),

  // Enforce critical patches on all online devices immediately
  enforcePatches: (severity = ['critical', 'high'], onlineOnly = true) =>
    axios.post(`${BASE}/tasks/enforce`, { severity, onlineOnly }, { headers: getHeaders() }),

  // Compliance
  getCompliance: () =>
    axios.get(`${BASE}/patches/compliance`, { headers: getHeaders() }),

  // P&V Compliance
  getComplianceSummary: () =>
    axios.get(`${BASE}/compliance/summary`, { headers: getHeaders() }),

  runEvaluation: (deviceId) =>
    axios.post(`${BASE}/compliance/evaluate`, deviceId ? { deviceId } : {}, { headers: getHeaders() }),

  getVulnerabilities: () =>
    axios.get(`${BASE}/compliance/vulnerabilities`, { headers: getHeaders() }),

  getUnsupported: () =>
    axios.get(`${BASE}/compliance/unsupported`, { headers: getHeaders() }),

  // Patch Releases
  getPatchReleases: (limit = 50) =>
    axios.get(`${BASE}/patches/releases?limit=${limit}`, { headers: getHeaders() }),

  getLatestReleases: () =>
    axios.get(`${BASE}/patches/releases/latest`, { headers: getHeaders() }),

  getPendingDevices: () =>
    axios.get(`${BASE}/patches/releases/pending-devices`, { headers: getHeaders() }),

  triggerReleaseFetch: () =>
    axios.post(`${BASE}/patches/releases/trigger-fetch`, {}, { headers: getHeaders() }),

  getFetcherStatus: () =>
    axios.get(`${BASE}/patches/fetcher-status`, { headers: getHeaders() }),

  // Emergency patches
  triggerEmergency: (patchId, deviceIds, triggeredBy) =>
    axios.post(`${BASE}/tasks/emergency`, { patchId, deviceIds, triggeredBy }, { headers: getHeaders() }),

  getEmergencyStatus: (patchId) =>
    axios.get(`${BASE}/tasks/emergency/status/${patchId}`, { headers: getHeaders() }),

  // Rollback
  triggerRollback: (deviceId, patchId, triggeredBy) =>
    axios.post(`${BASE}/tasks/rollback`, { deviceId, patchId, triggeredBy }, { headers: getHeaders() }),

  getRollbackHistory: () =>
    axios.get(`${BASE}/tasks/rollback/history`, { headers: getHeaders() }),

  // Asset Discovery
  getAssetInventory: () =>
    axios.get(`${BASE}/agents/inventory`, { headers: getHeaders() }),

  getDeviceSoftware: (deviceId, params = {}) =>
    axios.get(`${BASE}/agents/${deviceId}/software`, { headers: getHeaders(), params }),

  // Approval Matrix
  getApprovals: (params = {}) =>
    axios.get(`${BASE}/approvals`, { headers: getHeaders(), params }),

  getApprovalMatrix: () =>
    axios.get(`${BASE}/approvals/matrix`, { headers: getHeaders() }),

  createApproval: (data) =>
    axios.post(`${BASE}/approvals`, data, { headers: getHeaders() }),

  approveRequest: (id, data) =>
    axios.post(`${BASE}/approvals/${id}/approve`, data, { headers: getHeaders() }),

  rejectRequest: (id, data) =>
    axios.post(`${BASE}/approvals/${id}/reject`, data, { headers: getHeaders() }),

  cancelRequest: (id) =>
    axios.post(`${BASE}/approvals/${id}/cancel`, {}, { headers: getHeaders() }),

  // Diagnostics (includes server network interfaces)
  getDiagnostics: () =>
    axios.get(`/api/diagnostics`, { headers: getHeaders() }),

  // ── Patch Rings & Maintenance Schedule ────────────────────────────────
  getRings: () =>
    axios.get(`${BASE}/schedule/rings`, { headers: getHeaders() }),

  // ── Audit Log ─────────────────────────────────────────────────────────
  getAuditLogs: (params = {}) =>
    axios.get(`${BASE}/audit`, { headers: getHeaders(), params }),

  getAuditSummary: () =>
    axios.get(`${BASE}/audit/summary`, { headers: getHeaders() }),

  exportAuditLogs: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return `${BASE}/audit/export${query ? '?' + query : ''}`;
  },
  assignRing: (deviceId, ring, deviceType) =>
    axios.put(`${BASE}/schedule/rings/${deviceId}`, { ring, deviceType }, { headers: getHeaders() }),

  bulkAssignRing: (deviceIds, ring, deviceType) =>
    axios.post(`${BASE}/schedule/rings/bulk`, { deviceIds, ring, deviceType }, { headers: getHeaders() }),

  getMaintenanceWindows: () =>
    axios.get(`${BASE}/schedule/windows`, { headers: getHeaders() }),

  createMaintenanceWindow: (data) =>
    axios.post(`${BASE}/schedule/windows`, data, { headers: getHeaders() }),

  updateMaintenanceWindow: (id, data) =>
    axios.put(`${BASE}/schedule/windows/${id}`, data, { headers: getHeaders() }),

  deleteMaintenanceWindow: (id) =>
    axios.delete(`${BASE}/schedule/windows/${id}`, { headers: getHeaders() }),

  getCurrentWindowStatus: () =>
    axios.get(`${BASE}/schedule/current`, { headers: getHeaders() }),

  // ── AI Priority ───────────────────────────────────────────────────────
  getAiPriorities: (params = {}) =>
    axios.get(`${BASE}/ai-priority`, { headers: getHeaders(), params }),

  getAiPrioritySummary: () =>
    axios.get(`${BASE}/ai-priority/summary`, { headers: getHeaders() }),

  getAiTopPriorities: (n = 5) =>
    axios.get(`${BASE}/ai-priority/top/${n}`, { headers: getHeaders() }),

  getPatchAiPriority: (patchId) =>
    axios.get(`${BASE}/ai-priority/patch/${patchId}`, { headers: getHeaders() }),

  refreshAiPriority: () =>
    axios.post(`${BASE}/ai-priority/refresh`, {}, { headers: getHeaders() }),
  getEolAlerts: () =>
    axios.get(`${BASE}/eol/alerts`, { headers: getHeaders() }),

  runEolCheck: () =>
    axios.post(`${BASE}/eol/check`, {}, { headers: getHeaders() }),

  checkProduct: (product, version) =>
    axios.post(`${BASE}/eol/check-product`, { product, version }, { headers: getHeaders() }),

  // ── Email / Notification Settings ────────────────────────────────────
  getNotificationSettings: () =>
    axios.get(`${BASE}/notifications/settings`, { headers: getHeaders() }),

  saveNotificationSettings: (data) =>
    axios.post(`${BASE}/notifications/settings`, data, { headers: getHeaders() }),

  testSmtp: () =>
    axios.post(`${BASE}/notifications/test`, {}, { headers: getHeaders() }),

  sendMonthlyReport: () =>
    axios.post(`${BASE}/notifications/send-report`, {}, { headers: getHeaders() }),
  // ── Gemini AI Chat ────────────────────────────────────
  aiChat: (message, history = []) =>
    axios.post(`${BASE}/ai/chat`, { message, history }, { headers: getHeaders() }),
  getAiSummary: () =>
    axios.get(`${BASE}/ai/summary`, { headers: getHeaders() }),

  // ── Enrollment (QR Code) ──────────────────────────────────────────────
  getEnrollmentTokens: () =>
    axios.get(`${BASE}/enrollment/tokens`, { headers: getHeaders() }),
  createEnrollmentToken: (data) =>
    axios.post(`${BASE}/enrollment/tokens`, data, { headers: getHeaders() }),
  updateEnrollmentToken: (id, data) =>
    axios.put(`${BASE}/enrollment/tokens/${id}`, data, { headers: getHeaders() }),
  deleteEnrollmentToken: (id) =>
    axios.delete(`${BASE}/enrollment/tokens/${id}`, { headers: getHeaders() }),
  getEnrollmentQR: (id) =>
    axios.get(`${BASE}/enrollment/tokens/${id}/qr`, { headers: getHeaders() }),

  // ── Remote Actions ────────────────────────────────────────────────────
  queueRemoteAction: (deviceIds, type, payload) =>
    axios.post(`${BASE}/remote-actions`, { deviceIds, type, payload }, { headers: getHeaders() }),
  getRemoteActions: (params = {}) =>
    axios.get(`${BASE}/remote-actions`, { headers: getHeaders(), params }),
  cancelRemoteAction: (id) =>
    axios.delete(`${BASE}/remote-actions/${id}`, { headers: getHeaders() }),

  // ── File Store ────────────────────────────────────────────────────────
  getFiles: () =>
    axios.get(`${BASE}/file-store`, { headers: getHeaders() }),
  uploadFile: (data) =>
    axios.post(`${BASE}/file-store`, data, { headers: getHeaders() }),
  deleteFile: (id) =>
    axios.delete(`${BASE}/file-store/${id}`, { headers: getHeaders() }),
  deployFile: (id, deviceIds) =>
    axios.post(`${BASE}/file-store/${id}/deploy`, { deviceIds }, { headers: getHeaders() }),

  // ── Device Profiles ───────────────────────────────────────────────────
  getProfiles: () =>
    axios.get(`${BASE}/profiles`, { headers: getHeaders() }),
  createProfile: (data) =>
    axios.post(`${BASE}/profiles`, data, { headers: getHeaders() }),
  updateProfile: (id, data) =>
    axios.put(`${BASE}/profiles/${id}`, data, { headers: getHeaders() }),
  deleteProfile: (id) =>
    axios.delete(`${BASE}/profiles/${id}`, { headers: getHeaders() }),
  assignProfile: (id, deviceIds) =>
    axios.post(`${BASE}/profiles/${id}/assign`, { deviceIds }, { headers: getHeaders() }),
  getDeviceProfiles: (deviceId) =>
    axios.get(`${BASE}/profiles/device/${deviceId}`, { headers: getHeaders() }),

  // ── Jobs ──────────────────────────────────────────────────────────────
  getJobs: () =>
    axios.get(`${BASE}/jobs`, { headers: getHeaders() }),
  createJob: (data) =>
    axios.post(`${BASE}/jobs`, data, { headers: getHeaders() }),
  updateJob: (id, data) =>
    axios.put(`${BASE}/jobs/${id}`, data, { headers: getHeaders() }),
  deleteJob: (id) =>
    axios.delete(`${BASE}/jobs/${id}`, { headers: getHeaders() }),
  runJob: (id) =>
    axios.post(`${BASE}/jobs/${id}/run`, {}, { headers: getHeaders() }),
  getJobRuns: (id) =>
    axios.get(`${BASE}/jobs/${id}/runs`, { headers: getHeaders() }),

  // ── Reports ───────────────────────────────────────────────────────────
  getReportTypes: () =>
    axios.get(`${BASE}/reports/types`, { headers: getHeaders() }),
  generateReport: (type, format = 'json') =>
    axios.get(`${BASE}/reports/generate/${type}?format=${format}`, { headers: getHeaders() }),

  // ── Webhooks ──────────────────────────────────────────────────────────────
  getWebhooks: () => axios.get(`${BASE}/webhooks`, { headers: getHeaders() }),
  createWebhook: (data) => axios.post(`${BASE}/webhooks`, data, { headers: getHeaders() }),
  updateWebhook: (id, data) => axios.put(`${BASE}/webhooks/${id}`, data, { headers: getHeaders() }),
  deleteWebhook: (id) => axios.delete(`${BASE}/webhooks/${id}`, { headers: getHeaders() }),
  testWebhook: (id) => axios.post(`${BASE}/webhooks/${id}/test`, {}, { headers: getHeaders() }),
  getWebhookDeliveries: (id) => axios.get(`${BASE}/webhooks/${id}/deliveries`, { headers: getHeaders() }),

  // ── Conditional Access ────────────────────────────────────────────────────
  getCARules: () => axios.get(`${BASE}/conditional-access/rules`, { headers: getHeaders() }),
  createCARule: (data) => axios.post(`${BASE}/conditional-access/rules`, data, { headers: getHeaders() }),
  updateCARule: (id, data) => axios.put(`${BASE}/conditional-access/rules/${id}`, data, { headers: getHeaders() }),
  deleteCARule: (id) => axios.delete(`${BASE}/conditional-access/rules/${id}`, { headers: getHeaders() }),
  evaluateCA: (deviceId) => axios.post(`${BASE}/conditional-access/evaluate`, deviceId?{deviceId}:{}, { headers: getHeaders() }),
  getCAStates: () => axios.get(`${BASE}/conditional-access/states`, { headers: getHeaders() }),

  // ── Device Groups ─────────────────────────────────────────────────────────
  getDeviceGroups: () => axios.get(`${BASE}/device-groups`, { headers: getHeaders() }),
  createDeviceGroup: (data) => axios.post(`${BASE}/device-groups`, data, { headers: getHeaders() }),
  updateDeviceGroup: (id, data) => axios.put(`${BASE}/device-groups/${id}`, data, { headers: getHeaders() }),
  deleteDeviceGroup: (id) => axios.delete(`${BASE}/device-groups/${id}`, { headers: getHeaders() }),
  addGroupMembers: (id, deviceIds) => axios.post(`${BASE}/device-groups/${id}/members`, { deviceIds }, { headers: getHeaders() }),
  getGroupDevices: (id) => axios.get(`${BASE}/device-groups/${id}/devices`, { headers: getHeaders() }),
  getTags: () => axios.get(`${BASE}/device-groups/tags`, { headers: getHeaders() }),
  addTag: (deviceIds, key, value) => axios.post(`${BASE}/device-groups/tags`, { deviceIds, key, value }, { headers: getHeaders() }),
  bulkEnroll: (csv, defaultRing) => axios.post(`${BASE}/device-groups/bulk-enroll`, { csv, defaultRing }, { headers: getHeaders() }),

  // ── API Keys ──────────────────────────────────────────────────────────────
  getApiKeys: () => axios.get(`${BASE}/api-keys`, { headers: getHeaders() }),
  createApiKey: (data) => axios.post(`${BASE}/api-keys`, data, { headers: getHeaders() }),
  revokeApiKey: (id) => axios.delete(`${BASE}/api-keys/${id}`, { headers: getHeaders() }),

  // ── Scripting ─────────────────────────────────────────────────────────────
  getScriptTemplates: () => axios.get(`${BASE}/scripting/templates`, { headers: getHeaders() }),
  createScriptTemplate: (data) => axios.post(`${BASE}/scripting/templates`, data, { headers: getHeaders() }),
  deleteScriptTemplate: (id) => axios.delete(`${BASE}/scripting/templates/${id}`, { headers: getHeaders() }),
  runScript: (templateId, deviceIds, parameters) => axios.post(`${BASE}/scripting/run`, { templateId, deviceIds, parameters }, { headers: getHeaders() }),
  getScriptRuns: (limit) => axios.get(`${BASE}/scripting/runs?limit=${limit||100}`, { headers: getHeaders() }),
  getDeviceScriptRuns: (deviceId) => axios.get(`${BASE}/scripting/runs/device/${deviceId}`, { headers: getHeaders() }),

  // ── SSO ───────────────────────────────────────────────────────────────────
  getSsoConfigs: () => axios.get(`${BASE}/sso/configs`, { headers: getHeaders() }),
  saveSsoConfig: (data) => axios.post(`${BASE}/sso/configs`, data, { headers: getHeaders() }),
  testSsoConfig: (id) => axios.get(`${BASE}/sso/configs/${id}/test`, { headers: getHeaders() }),
  deleteSsoConfig: (id) => axios.delete(`${BASE}/sso/configs/${id}`, { headers: getHeaders() }),

  // ── Organizations ─────────────────────────────────────────────────────────
  getOrgs: () => axios.get(`${BASE}/orgs`, { headers: getHeaders() }),
  createOrg: (data) => axios.post(`${BASE}/orgs`, data, { headers: getHeaders() }),
  getOrgStats: (id) => axios.get(`${BASE}/orgs/${id}/stats`, { headers: getHeaders() }),
  getCurrentOrg: () => axios.get(`${BASE}/orgs/current`, { headers: getHeaders() }),

  // ── Geofencing ────────────────────────────────────────────────────────────
  getGeofences: () => axios.get(`${BASE}/geofencing`, { headers: getHeaders() }),
  createGeofence: (data) => axios.post(`${BASE}/geofencing`, data, { headers: getHeaders() }),
  deleteGeofence: (id) => axios.delete(`${BASE}/geofencing/${id}`, { headers: getHeaders() }),

  // ── MDM ───────────────────────────────────────────────────────────────────
  getMdmStatus: () => axios.get(`${BASE}/mdm/status`, { headers: getHeaders() }),
  getMdmPlatforms: () => axios.get(`${BASE}/mdm/platforms`, { headers: getHeaders() }),

  // ── Push Notifications ────────────────────────────────────────────────────
  getPushConfigs: () => axios.get(`${BASE}/push/configs`, { headers: getHeaders() }),
  savePushConfig: (data) => axios.post(`${BASE}/push/configs`, data, { headers: getHeaders() }),
  sendPush: (deviceIds, message) => axios.post(`${BASE}/push/send`, { deviceIds, message }, { headers: getHeaders() }),

  // ── SCIM ──────────────────────────────────────────────────────────────────
  getScimConfig: () => axios.get(`${BASE}/scim/config`, { headers: getHeaders() }),
  getScimUsers: () => axios.get(`${BASE}/scim/v2/Users`, { headers: getHeaders() }),
  getScimGroups: () => axios.get(`${BASE}/scim/v2/Groups`, { headers: getHeaders() }),
};

